import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { AVIF_TYPE, WEBP_TYPE, avifFor, dimsFor, srcSetFor } from "@/lib/image-formats";


type Ratio = "portrait" | "product" | "square" | "landscape" | "hero" | "tall" | "auto";

const ratioClass: Record<Ratio, string> = {
  portrait: "aspect-[3/4]",
  product: "aspect-[4/5]",
  square: "aspect-square",
  landscape: "aspect-[16/10]",
  hero: "aspect-[4/5] sm:aspect-[16/9]",
  tall: "aspect-[2/3]",
  auto: "",
};

type Props = {
  /** Real photography source. When empty an elegant placeholder frame is shown. */
  src?: string | undefined;
  alt: string;
  ratio?: Ratio | undefined;
  label?: string | undefined;
  className?: string | undefined;
  imageClassName?: string | undefined;
  priority?: boolean | undefined;
  /** Responsive `sizes` hint so the browser downloads the smallest useful image. */
  sizes?: string | undefined;
  /** Hide the placeholder caption (used behind full-bleed overlay text). */
  hideLabel?: boolean | undefined;
  /** AVIF source explicite (assets bundlés dont l'URL est hashée par Vite). */
  avifSrc?: string | undefined;
  /** Object-fit behaviour: "cover" (default) or "contain" for clean framed product shots. */
  fit?: "cover" | "contain" | undefined;
};


/**
 * Single image primitive for the whole site.
 * Swap in real Sunset Concept Store photography by passing `src` — the aspect ratio and
 * object-fit behaviour stay identical everywhere.
 */
export function EditorialImage({
  src,
  alt,
  ratio = "portrait",
  label,
  className,
  imageClassName,
  priority,
  sizes,
  hideLabel,
  avifSrc,
  fit = "cover",
}: Props) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete) {
      setLoaded(true);
    }
  }, [src]);

  // Négociation de format : AVIF si le navigateur le supporte, WebP sinon.
  const avif = avifSrc ?? avifFor(src);
  // Déclinaisons de largeurs : le navigateur choisit la plus petite utile.
  const avifSet = srcSetFor(src, "avif");
  const webpSet = srcSetFor(src, "webp");
  const dims = dimsFor(src);
  const resolvedSizes = sizes ?? "100vw";

  return (
    <div
      className={cn("media-frame bg-sand", ratioClass[ratio], className)}
      {...(ratio === "auto"
        ? { style: { aspectRatio: dims ? `${dims.width} / ${dims.height}` : "4 / 5" } }
        : {})}
    >
      {src ? (
        <>
          {/* Skeleton de hauteur fixe : occupe tout le cadre tant que la photo n'est pas chargée */}
          {!loaded ? (
            <div
              aria-hidden="true"
              className="skeleton-shimmer absolute inset-0 h-full w-full"
            />
          ) : null}
          <picture>
            {avif ? (
              <source
                type={AVIF_TYPE}
                srcSet={avifSet ?? avif}
                sizes={resolvedSizes}
                {...(dims ?? {})}
              />
            ) : null}
            {webpSet ? (
              <source
                type={WEBP_TYPE}
                srcSet={webpSet}
                sizes={resolvedSizes}
                {...(dims ?? {})}
              />
            ) : null}
            <img
              ref={imgRef}
              src={src}
              alt={alt}
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={priority ? "high" : "auto"}
              sizes={resolvedSizes}
              {...(dims ?? {})}
              onLoad={() => setLoaded(true)}
              onError={() => setLoaded(true)}
              className={cn(
                "media-zoom absolute inset-0 h-full w-full object-center transition-opacity duration-500",
                fit === "contain" ? "object-contain" : "object-cover",
                loaded ? "opacity-100" : "opacity-0",
                imageClassName,
              )}
            />
          </picture>
        </>

      ) : (
        <div
          role="img"
          aria-label={alt}
          className="media-zoom flex h-full w-full items-center justify-center bg-gradient-to-br from-secondary via-muted to-secondary"
        >
          {hideLabel ? null : (
            <span className="label-xs px-6 text-center text-muted-foreground/70">
              {label ?? alt}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
